import React, { useEffect, forwardRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { Modal, Button, Form, Row, Col, Spinner, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Autocomplete, TextField } from "@mui/material";
import DatePicker from 'react-datepicker';
import es from 'date-fns/locale/es';
import { useDropzone } from 'react-dropzone';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';
import ModalDescargaNotaVenta from '../ModalDescargaNotaVenta';

import useGastoAPI from './useGastoAPI';
import useGastoData from './useGastoData';

const ModalRegistroGastoNuevo = ({
    setGastos,
    gastos,
    setSelectedGasto,
    selectedGasto,
    setUltimoGasto,
    ultimoGasto,
    copia,
    setCopia,
    Editar,
    setEditar,
    showModalNuevo,
    showModalRegistrarGasto,
    setShowModalRegistrarGasto
}) => {
    const { handleSubmit, reset } = useForm();
    const [guardando, setGuardando] = useState(false);

    // Custom hooks
    const {
        formasdePago,
        cuentasBancarias,
        responsableUsuario,
        tipoDeGasto,
        registrarGasto,
        inicializarDatos
    } = useGastoAPI();

    const {
        formData,
        setFormData,
        formErrors,
        date,
        setDate,
        notaDeVenta,
        setNotaDeVenta,
        tipoDeCambio,
        handleChange,
        handleDateChange,
        obtenerTipoDeCambio,
        validarDatosGasto,
        seleccionarArchivoNotaPago,
        prepararDatosGasto
    } = useGastoData(selectedGasto, ultimoGasto);

    const opcionesDescripcion = Array.from(new Set((gastos || []).map(gasto => gasto.vDescripcion)));

    useEffect(() => {
        obtenerTipoDeCambio();
        inicializarDatos();
    }, []);

    useEffect(() => {
        if (copia && selectedGasto && Object.keys(selectedGasto).length > 0) {
            const fechaOriginal = selectedGasto.dFechaGasto ? new Date(selectedGasto.dFechaGasto) : new Date();
            setFormData({
                ...selectedGasto,
                idGasto: 0,
                dFechaGasto: fechaOriginal
            });
            setDate(fechaOriginal);
        }
    }, [copia, selectedGasto, setDate, setFormData]);

    const GreetingsDateInput = forwardRef(({ value, onClick }, ref) => (
        <div className="position-relative">
            <Form.Control
                size="sm"
                ref={ref}
                onClick={onClick}
                value={value || ""}
                placeholder="dd/mm/aaaa"
                className="ps-3 bg-light border-0 shadow-sm"
                readOnly
                style={{ height: '38px', borderRadius: '0.375rem' }}
            />
            <FontAwesomeIcon
                icon="calendar-alt"
                className="text-secondary position-absolute top-50 end-0 translate-middle-y me-3"
                style={{ cursor: 'pointer' }}
                onClick={onClick}
            />
        </div>
    ));

    GreetingsDateInput.displayName = 'GreetingsDateInput';
    GreetingsDateInput.propTypes = {
        value: PropTypes.string,
        onClick: PropTypes.func,
    };

    const onSubmit = async () => {
        if (!validarDatosGasto()) {
            return;
        }

        const datosGasto = prepararDatosGasto();

        const callbacks = {
            setGuardando,
            setEditar,
            setCargando: setGuardando,
            setSelectedGasto,
            setShowModalRegistrarGasto,
            setGastos,
            setUltimoGasto
        };

        await registrarGasto(datosGasto, callbacks);
    };

    const handleClose = () => {
        if (Editar && copia) {
            setShowModalRegistrarGasto(false);
            setSelectedGasto([]);
            setEditar(false);
            setCopia(false);
        } else if (Editar) {
            setEditar(false);
            setDate(null);
            setShowModalRegistrarGasto(false);
            reset();
            setSelectedGasto({});
        } else {
            setSelectedGasto({});
            setShowModalRegistrarGasto(false);
        }
    };

    // Dropzone logic
    const onDrop = useCallback((acceptedFiles) => {
        if (acceptedFiles && acceptedFiles.length > 0) {
            const file = acceptedFiles[0];
            const fakeEvent = {
                target: {
                    files: [file]
                }
            };
            seleccionarArchivoNotaPago(fakeEvent);
        }
    }, [seleccionarArchivoNotaPago]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        multiple: false
    });

    const { token } = useToken();
    const { showErrorToast } = useToast();
    const [showDescargaModal, setShowDescargaModal] = useState(false);

    const descargarNotaDesdeApi = async (idGasto) => {
        setShowDescargaModal(true);
        try {
            const response = await fetch(`/api/Gastos/ObtenerNotaVenta?idGasto=${idGasto}`, {
                method: 'GET',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
            });

            if (response.ok) {
                const data = await response.json();
                if (data.iResponseType === 1) {
                    const detalles = JSON.parse(data.detalles);
                    const { vNotaVenta_Base64, vNotaVenta_Name } = detalles.table2[0];
                    const byteCharacters = atob(vNotaVenta_Base64);
                    const byteNumbers = new Array(byteCharacters.length);
                    for (let i = 0; i < byteCharacters.length; i++) {
                        byteNumbers[i] = byteCharacters.charCodeAt(i);
                    }
                    const byteArray = new Uint8Array(byteNumbers);
                    const blob = new Blob([byteArray], { type: 'application/octet-stream' });

                    const link = document.createElement('a');
                    link.href = URL.createObjectURL(blob);
                    link.download = vNotaVenta_Name;
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    URL.revokeObjectURL(link.href);
                } else {
                    showErrorToast(data.vMessage || 'Error al obtener nota de venta');
                }
            } else {
                showErrorToast('Error en la petición: ' + response.statusText);
            }
        } catch (error) {
            showErrorToast('Error al descargar nota de venta');
            console.error('Error al descargar nota de venta:', error);
        } finally {
            setShowDescargaModal(false);
        }
    };

    const descargarNotaLocal = (base64String, fileName) => {
        try {
            const byteCharacters = atob(base64String);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'application/octet-stream' });

            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.href = url;
            link.download = fileName || 'nota';
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
        } catch (error) {
            showErrorToast('Error al descargar el archivo local');
            console.error(error);
        }
    };

    const handleDownloadClick = () => {
        if (notaDeVenta && notaDeVenta.file && notaDeVenta.fileName) {
            setShowDescargaModal(true);
            try {
                descargarNotaLocal(notaDeVenta.file, notaDeVenta.fileName);
            } finally {
                setTimeout(() => setShowDescargaModal(false), 600);
            }
        } else if (selectedGasto?.bHasNotaVenta === 1) {
            descargarNotaDesdeApi(selectedGasto.idGasto);
        }
    };

    const isReadOnly = !showModalNuevo && !Editar;

    // Parse numeric fields safely
    const parseNum = (val) => isNaN(parseFloat(val)) ? 0 : parseFloat(val);
    const importeBruto = parseNum(formData.dImporte);
    const descuento = parseNum(formData.dDescuento);
    const iva = parseNum(formData.dIva) || (parseNum(formData.dCantidad) * parseNum(formData.dPrecioUnitario) * (parseNum(formData.dTasaCuotaIva) / 100));
    const retenciones = parseNum(formData.dIvaRet) + parseNum(formData.dIsrRet);
    const totalNeto = parseNum(formData.dTotal) || (importeBruto - descuento + iva - retenciones);

    return (
        <Modal
            show={showModalRegistrarGasto}
            size="xl"
            backdrop="static"
            centered
            keyboard={false}
            onHide={handleClose}
        >
            <div className="bg-white dark__bg-1100 overflow-hidden w-100 shadow rounded-3">
                <Modal.Header closeButton className="pb-3 pt-3 px-4 border-bottom">
                    <Modal.Title className="fs-5 fw-medium d-flex align-items-center text-dark">
                        <FontAwesomeIcon icon="file-invoice-dollar" className="me-2 text-primary" />
                        {showModalNuevo ? 'Registrar Gasto' : (copia ? 'Copia Gasto' : 'Detalle de Gasto')}
                    </Modal.Title>
                </Modal.Header>

                <Form onSubmit={handleSubmit(onSubmit)}>
                    <Modal.Body className="px-4 py-3">
                        <Row className="g-4">
                            {/* Columna Izquierda (70%) */}
                            <Col lg={8}>
                                {/* Información General */}
                                <h6 className="fw-bold text-secondary mb-3">Información General</h6>

                                <Form.Group className="mb-3">
                                    <Form.Label className="text-muted small mb-1">Descripción del Gasto</Form.Label>
                                    {isReadOnly ? (
                                        <Form.Control as="textarea" rows={3} size="sm" value={selectedGasto?.vDescripcion || ""} className="bg-light" readOnly style={{ resize: 'none' }} />
                                    ) : (
                                        <Autocomplete
                                            freeSolo
                                            options={opcionesDescripcion}
                                            value={formData.vDescripcion || ""}
                                            onInputChange={(event, newValue) => handleChange("vDescripcion", newValue?.toUpperCase() || '')}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    multiline
                                                    rows={3}
                                                    size="small"
                                                    autoComplete="off"
                                                    error={!!formErrors.vDescripcion}
                                                    helperText={formErrors.vDescripcion}
                                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '0.375rem', padding: '8.5px 14px' } }}
                                                    placeholder="Escribe el concepto del gasto..."
                                                />
                                            )}
                                        />
                                    )}
                                </Form.Group>

                                <Row className="g-3 mb-3">
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label className="text-muted small mb-1">Tipo de Gasto</Form.Label>
                                            {isReadOnly ? (
                                                <Form.Control size="sm" type="text" value={selectedGasto?.vTipoGasto || ""} className="bg-light" readOnly />
                                            ) : (
                                                <Form.Select value={formData.idTipoGasto || ""} onChange={(e) => handleChange("idTipoGasto", e.target.value)} isInvalid={!!formErrors.idTipoGasto}>
                                                    <option value="">Selecciona tipo de gasto</option>
                                                    {tipoDeGasto?.map((tipo) => <option key={tipo.idTipoGasto} value={tipo.idTipoGasto}>{tipo.vNombre}</option>)}
                                                </Form.Select>
                                            )}
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label className="text-muted small mb-1">Fecha de Gasto</Form.Label>
                                            {isReadOnly ? (
                                                <Form.Control size="sm" type="text" value={selectedGasto?.dFechaGasto ? new Date(selectedGasto.dFechaGasto).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '/') : ""} className="bg-light" readOnly />
                                            ) : (
                                                <DatePicker
                                                    selected={date}
                                                    onChange={handleDateChange}
                                                    customInput={<GreetingsDateInput />}
                                                    dateFormat="dd/MMM/yyyy"
                                                    locale={es}
                                                    wrapperClassName="w-100 notranslate"
                                                    calendarClassName="notranslate"
                                                />
                                            )}
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row className="g-3 mb-4">
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label className="text-muted small mb-1">Cuenta Bancaria</Form.Label>
                                            {isReadOnly ? (
                                                <Form.Control size="sm" type="text" value={selectedGasto?.vCuentaBancaria || ""} className="bg-light" readOnly />
                                            ) : (
                                                <Form.Select

                                                    value={formData.idCatCuentaBancaria || ""}
                                                    onChange={(e) => handleChange("idCatCuentaBancaria", e.target.value)}
                                                    isInvalid={!!formErrors.idCatCuentaBancaria}
                                                >
                                                    <option value="">Selecciona una cuenta</option>
                                                    {cuentasBancarias?.map((cuenta) => <option key={cuenta.idCatCuentaBancaria} value={cuenta.idCatCuentaBancaria}>{cuenta.vNumCta} - {cuenta.vNombreBanco} ({cuenta.vNombreTitular})</option>)}
                                                </Form.Select>
                                            )}
                                            <Form.Control.Feedback type="invalid">{formErrors.idCatCuentaBancaria}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label className="text-muted small mb-1">Responsable</Form.Label>
                                            {isReadOnly ? (
                                                <Form.Control size="sm" type="text" value={selectedGasto?.vUsuarioResponsable || ""} className="bg-light" readOnly />
                                            ) : (
                                                <Form.Select

                                                    value={formData.idUsuario || ""}
                                                    onChange={(e) => handleChange("idUsuario", e.target.value)}
                                                    isInvalid={!!formErrors.idUsuario}
                                                >
                                                    <option value="">Selecciona responsable</option>
                                                    {responsableUsuario?.map((usuario) => <option key={usuario.idUsuario} value={usuario.idUsuario}>{usuario.vNombre} {usuario.vApellidos}</option>)}
                                                </Form.Select>
                                            )}
                                            <Form.Control.Feedback type="invalid">{formErrors.idUsuario}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row className="g-3 mb-4">
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label className="text-muted small mb-1">Forma de Pago</Form.Label>
                                            {isReadOnly ? (
                                                <Form.Control size="sm" type="text" value={selectedGasto?.vFormaPago || ""} className="bg-light" readOnly />
                                            ) : (
                                                <Form.Select

                                                    value={formData.idCatFormaPago || ""}
                                                    onChange={(e) => handleChange("idCatFormaPago", e.target.value)}
                                                    isInvalid={!!formErrors.idCatFormaPago}
                                                >
                                                    <option value="">Selecciona forma de pago</option>
                                                    {formasdePago?.map((forma) => <option key={forma.vClave} value={forma.vClave}>{`${forma.vClave} - ${forma.vDescripcionFormaPago}`}</option>)}
                                                </Form.Select>
                                            )}
                                            <Form.Control.Feedback type="invalid">{formErrors.idCatFormaPago}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                {/* Detalles Financieros */}
                                <h6 className="fw-bold text-secondary mb-3 mt-4">Detalles Financieros</h6>

                                <Row className="g-3 mb-3">
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label className="text-muted small mb-1">Cantidad</Form.Label>
                                            <Form.Control className="text-end" type="number" step="any" value={formData.dCantidad || ""} onChange={(e) => handleChange("dCantidad", e.target.value)} readOnly={isReadOnly} isInvalid={!!formErrors.dCantidad} />
                                            <Form.Control.Feedback type="invalid">{formErrors.dCantidad}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label className="text-muted small mb-1">Precio Unit.</Form.Label>
                                            <Form.Control className="text-end" type="number" step="any" value={formData.dPrecioUnitario || ""} onChange={(e) => handleChange("dPrecioUnitario", e.target.value)} readOnly={isReadOnly} isInvalid={!!formErrors.dPrecioUnitario} />
                                            <Form.Control.Feedback type="invalid">{formErrors.dPrecioUnitario}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label className="text-muted small mb-1">Importe</Form.Label>
                                            <Form.Control className="text-end bg-light" type="number" step="any" value={formData.dImporte || ""} readOnly />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label className="text-muted small mb-1">Desc.</Form.Label>
                                            <Form.Control className="text-end" type="number" step="any" value={formData.dDescuento || ""} onChange={(e) => handleChange("dDescuento", e.target.value)} readOnly={isReadOnly} />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row className="g-3 mb-3">
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label className="text-muted small mb-1">Moneda</Form.Label>
                                            {isReadOnly ? (
                                                <Form.Control size="sm" type="text" value={formData.vMoneda || ""} className="bg-light" readOnly />
                                            ) : (
                                                <Form.Select

                                                    value={formData.idTipoMoneda || ""}
                                                    onChange={(e) => {
                                                        const nuevaMoneda = e.target.value;
                                                        handleChange("idTipoMoneda", nuevaMoneda);
                                                        if (nuevaMoneda === "1") {
                                                            handleChange("dTipoCambio", 1);
                                                        } else {
                                                            handleChange("dTipoCambio", tipoDeCambio?.USD_CAMBIADO || 1);
                                                        }
                                                    }}
                                                >
                                                    <option value={1}>MXN</option>
                                                    <option value={2}>USD</option>
                                                </Form.Select>
                                            )}
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label className="text-muted small mb-1">T. Cambio</Form.Label>
                                            <Form.Control className="text-end" type="number" step="any" value={formData.dTipoCambio || ""} onChange={(e) => handleChange("dTipoCambio", e.target.value)} readOnly={isReadOnly || formData.idTipoMoneda === "1"} />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label className="text-muted small mb-1">Tipo Factor IVA</Form.Label>
                                            {isReadOnly ? (
                                                <Form.Control size="sm" type="text" value={selectedGasto?.vTipoFactorIva || ""} className="bg-light" readOnly />
                                            ) : (
                                                <Form.Select

                                                    value={formData.vTipoFactorIva || ""}
                                                    onChange={(e) => {
                                                        const selectedValue = e.target.value;
                                                        handleChange("vTipoFactorIva", selectedValue);
                                                        if (selectedValue === "Tasa") {
                                                            handleChange("dTasaCuotaIva", "0.00");
                                                        } else if (selectedValue === "Exento") {
                                                            handleChange("dTasaCuotaIva", "0");
                                                        } else {
                                                            handleChange("dTasaCuotaIva", "0");
                                                        }
                                                    }}
                                                    isInvalid={!!formErrors.vTipoFactorIva}
                                                >
                                                    <option value="">Selecciona tasa</option>
                                                    <option value="Tasa">Tasa</option>
                                                    <option value="Cuota">Cuota</option>
                                                    <option value="Exento">Exento</option>
                                                </Form.Select>
                                            )}
                                            <Form.Control.Feedback type="invalid">{formErrors.vTipoFactorIva}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label className="text-muted small mb-1">Tasa Cuota IVA (0-1)</Form.Label>
                                            <Form.Control className="text-end bg-light" type="number" step="any" value={formData.dTasaCuotaIva !== undefined && formData.dTasaCuotaIva !== null ? formData.dTasaCuotaIva : ""} disabled={formData.vTipoFactorIva === "Exento"} onChange={(e) => handleChange("dTasaCuotaIva", e.target.value)} readOnly={isReadOnly} isInvalid={!!formErrors.dTasaCuotaIva} />
                                            <Form.Control.Feedback type="invalid">{formErrors.dTasaCuotaIva}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row className="g-3">
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label className="text-muted small mb-1">IVA</Form.Label>
                                            <Form.Control className="text-end bg-light" type="number" step="any" value={formData.dIva || "0"} disabled={formData.vTipoFactorIva === "Exento"} readOnly />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label className="text-muted small mb-1">IVA Ret.</Form.Label>
                                            <Form.Control className="text-end" type="number" step="any" value={formData.dIvaRet || ""} onChange={(e) => handleChange("dIvaRet", e.target.value)} readOnly={isReadOnly} />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label className="text-muted small mb-1">ISR Ret.</Form.Label>
                                            <Form.Control className="text-end" type="number" step="any" value={formData.dIsrRet || ""} onChange={(e) => handleChange("dIsrRet", e.target.value)} readOnly={isReadOnly} />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label className="text-muted small mb-1">Total</Form.Label>
                                            <Form.Control className="text-end bg-light" type="number" step="any" value={formData.dTotal !== undefined && formData.dTotal !== null ? formData.dTotal : ""} readOnly />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Col>

                            {/* Columna Derecha (30%) */}
                            <Col lg={4} className="d-flex flex-column border-start ps-4">
                                <h6 className="fw-bold text-secondary mb-3">Nota de Venta (Opcional)</h6>

                                {!isReadOnly ? (
                                    <div
                                        {...getRootProps()}
                                        className="p-3 text-center rounded-3 mb-3 d-flex flex-column justify-content-center align-items-center"
                                        style={{
                                            minHeight: '100px',
                                            border: isDragActive ? '2px dashed #2c7be5' : '2px dashed #dee2e6',
                                            backgroundColor: isDragActive ? 'rgba(44, 123, 229, 0.1)' : 'transparent',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease'
                                        }}
                                    >
                                        <input {...getInputProps()} />
                                        <FontAwesomeIcon icon="file-upload" className="fs-3 text-primary mb-1 opacity-75" />
                                        <p className="mb-0 fw-semibold text-primary" style={{ fontSize: '0.9rem' }}>Subir archivos</p>
                                        <small className="text-muted" style={{ fontSize: '0.75rem' }}>PDF o Imagen hasta 3MB</small>
                                    </div>
                                ) : null}

                                {notaDeVenta?.fileName || (selectedGasto?.bHasNotaVenta === 1) ? (
                                    <div className="d-flex align-items-center justify-content-between p-3 bg-100 dark__bg-1100 border rounded shadow-sm mb-4">
                                        <div
                                            className="d-flex align-items-center overflow-hidden"
                                            style={{ cursor: 'pointer' }}
                                            onClick={handleDownloadClick}
                                            title="Clic para descargar / ver archivo"
                                        >
                                            <FontAwesomeIcon icon="file-pdf" className="text-danger me-3 fs-4" />
                                            <span className="text-truncate small fw-medium text-primary" style={{ maxWidth: '180px' }}>
                                                {notaDeVenta?.fileName || "Factura adjunta"}
                                            </span>
                                        </div>
                                        {!isReadOnly && (
                                            <Button variant="link" className="text-danger p-0" onClick={() => setNotaDeVenta({ file: null, fileName: '' })}>
                                                <FontAwesomeIcon icon="trash-alt" />
                                            </Button>
                                        )}
                                    </div>
                                ) : null}

                                {/* Totales Card */}
                                <Card className="border-0 mt-auto shadow-sm">
                                    <Card.Body className="p-4">
                                        <div className="d-flex justify-content-between mb-2 small text-muted">
                                            <span>Importe</span>
                                            <span className="fw-medium text-dark">${importeBruto.toFixed(2)}</span>
                                        </div>
                                        <div className="d-flex justify-content-between mb-2 small text-danger">
                                            <span>Descuento</span>
                                            <span className="fw-medium">-${descuento.toFixed(2)}</span>
                                        </div>
                                        <div className="d-flex justify-content-between mb-2 small text-muted">
                                            <span>IVA ({formData.dTasaCuotaIva || 0}%)</span>
                                            <span className="fw-medium text-dark">${iva.toFixed(2)}</span>
                                        </div>
                                        <div className="d-flex justify-content-between mb-3 small text-muted">
                                            <span>Retenciones (IVA/ISR)</span>
                                            <span className="fw-medium text-dark">${retenciones.toFixed(2)}</span>
                                        </div>

                                        <hr className="my-3 text-muted opacity-25" />

                                        <div className="mb-1">
                                            <small className="text-muted fw-bold d-block mb-1" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>TOTAL NETO (MXN)</small>
                                            <div className="fs-2 fw-bolder text-dark m-0 lh-1">${totalNeto.toFixed(2)}</div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Modal.Body>

                    <Modal.Footer className="border-0 px-4 pb-4 pt-2 d-flex justify-content-end bg-transparent">
                        <Button
                            variant="light"
                            className="text-secondary fw-medium me-2"
                            onClick={handleClose}
                        >
                            Cancelar
                        </Button>

                        <Button
                            type="submit"
                            variant="primary"
                            className="rounded-3 px-4 py-2 fw-medium d-flex align-items-center shadow-sm"
                            disabled={guardando}
                        >
                            {guardando ? (
                                <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" /> Guardando...</>
                            ) : (
                                <><FontAwesomeIcon icon="check" className="me-2" /> {copia ? 'Guardar Copia' : 'Guardar Registro'}</>
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            </div>
            <ModalDescargaNotaVenta show={showDescargaModal} />
        </Modal>
    );
};

ModalRegistroGastoNuevo.propTypes = {
    selectedGasto: PropTypes.object,
    setSelectedGasto: PropTypes.func,
    setUltimoGasto: PropTypes.func,
    ultimoGasto: PropTypes.object,
    setCopia: PropTypes.func,
    copia: PropTypes.bool,
    setEditar: PropTypes.func,
    Editar: PropTypes.bool,
    setGastos: PropTypes.func,
    gastos: PropTypes.array,
    showModalNuevo: PropTypes.bool,
    showModalRegistrarGasto: PropTypes.bool,
    setShowModalRegistrarGasto: PropTypes.func
};

export default ModalRegistroGastoNuevo;
